import React, { useState, useEffect, useOptimistic } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@clareefai/ui';
import PageTitle from '../../components/shared/PageTitle';
import EditRewardModal from '../../components/rewards/EditRewardModal';
import { useAbility } from '../../contexts/AbilityContext';
import AddFundRequestModal from '../../components/rewards/AddFundRequestForm';
import {
  useGetRewardSettingsQuery,
  useUpdateRewardSettingsMutation,
  useGetVendorMeQuery,
  useGetVendorRewardsQuery,
  useGetVendorRewardsSummaryQuery,
  useGetRewardAnalyticsQuery,
  useGetFundRequestsQuery,
  usePayPendingRewardMutation,
  useDeclinePendingRewardMutation,
  useGetVendorBalanceQuery,
  useResendGiftCardEmailMutation,
  useCancelGiftCardRewardMutation,
  useGetReclaimedRewardsQuery,
  useMarkDiscountAppliedMutation,
} from '../../store/vendorApi';
import { RewardConfig, RewardResponse } from '@clareefai/types';
import SpecificRewardsListTab from '../../components/rewards/specific/SpecificRewardsListTab';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Euro, Gift, TrendingUp, Users, Award,
  Search, Download, Eye, X, ExternalLink, HandCoins,
  CheckCircle, Clock, AlertCircle, CreditCard,
  BarChart3, PieChart, Target, Star, Video, FileText,
  Loader2, Wallet, Percent, Mail, Settings, ShieldCheck, RotateCcw, CalendarDays, Coins, XCircle, ChevronDown,
  Plus, Heart, Zap, Sparkles, Trophy, ThumbsUp, Smile, Coffee, Lightbulb, Music, Mic, BookOpen, MessageCircle
} from 'lucide-react';

// Tremendous's own dashboard is the only place a delivered-but-unredeemed reward can be cancelled
// within their 7-day window (the API restricts cancel to delivery failures). We deep-link vendors
// to the reward history there rather than pretend our Cancel button can do it.
const TREMENDOUS_DASHBOARD_URL = 'https://app.tremendous.com/rewards';

// Shared styling for the payment-row action controls. Every action — the solid buttons and the
// outlined Tremendous deep-link — renders through these so they share one height and shape.
// The fixed `h-8` is what keeps the bordered <a> from rendering taller than its borderless
// <button> siblings, and the outline uses an inset box-shadow ring (not a layout-affecting
// border) so its box model matches the solid buttons exactly. `pl-2.5 pr-3` gives the leading
// icon slightly tighter padding so the label looks optically centred.
const actionBtnBase =
  'inline-flex items-center gap-1.5 h-8 pl-2.5 pr-3 rounded-lg text-sm font-medium whitespace-nowrap ' +
  'transition-[transform,background-color,box-shadow,color,opacity] duration-150 ease-out ' +
  'active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';

const actionBtnVariants = {
  green: 'bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-500',
  red: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
  blue: 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500',
  outline:
    'bg-white text-gray-600 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.12)] ' +
    'hover:bg-gray-50 hover:text-gray-900 hover:shadow-[inset_0_0_0_1px_rgba(0,0,0,0.18)] ' +
    'focus-visible:ring-gray-400',
} as const;

// Square icon-only control (the row's view button) — same height + press/focus behaviour as the
// labelled actions, with a 32px hit target instead of the old cramped `p-1` icon.
const actionIconBtn =
  'inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 ' +
  'transition-[transform,background-color,color] duration-150 ease-out active:scale-[0.96] ' +
  'hover:text-blue-600 hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-offset-1 focus-visible:ring-blue-500';

const Rewards: React.FC = () => {
  const { t } = useTranslation();
  const ability = useAbility();
  const navigate = useNavigate();
  const location = useLocation();
  const canManageReward = ability.can('manage', 'Reward');
  const [searchParams, setSearchParams] = useSearchParams();
  type RewardsTab = 'overview' | 'rewards_list' | 'payments' | 'claims' | 'analytics' | 'funding' | 'specific_rewards';
  const REWARDS_TABS: RewardsTab[] = ['overview', 'rewards_list', 'payments', 'claims', 'analytics', 'funding', 'specific_rewards'];
  // The active tab is derived from the URL (?tab=) on every render rather than held in
  // local state, so deep-links from notifications (e.g. the "Reward awaiting your approval"
  // bell → Payments) switch tabs even when the vendor is already on /rewards — there's no
  // remount to re-read an initializer. Legacy location.state.activeTab is still honored as a
  // fallback, and anything unrecognized falls back to Overview.
  const isRewardsTab = (value: string | null | undefined): value is RewardsTab =>
    !!value && REWARDS_TABS.includes(value as RewardsTab);
  const stateTab = (location.state as { activeTab?: RewardsTab } | null)?.activeTab;
  const paramTab = searchParams.get('tab');
  const activeTab: RewardsTab = isRewardsTab(paramTab)
    ? paramTab
    : isRewardsTab(stateTab)
      ? stateTab
      : 'overview';
  const setActiveTab = (tab: RewardsTab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  };
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRewardType, setSelectedRewardType] = useState<any>(null);
  const [fundFilterStatus, setFundFilterStatus] = useState('all');
  const [showAddFundRequestModal, setShowAddFundRequestModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<RewardResponse | null>(null);

  // API Queries
  const { data: rewardSettings, isLoading: isLoadingSettings, refetch: refetchSettings } = useGetRewardSettingsQuery();
  const { data: vendor } = useGetVendorMeQuery();
  const { data: rewardsData, isLoading: isLoadingRewards } = useGetVendorRewardsQuery({ page: 0, size: 100 });
  const { data: rewardsSummary } = useGetVendorRewardsSummaryQuery();
  const { data: rewardAnalytics, isLoading: isLoadingAnalytics } = useGetRewardAnalyticsQuery();
  const { data: fundRequestsData, isLoading: isLoadingFundRequests } = useGetFundRequestsQuery();
  const { data: balanceData } = useGetVendorBalanceQuery();
  const [updateRewardSettings] = useUpdateRewardSettingsMutation();
  const [payPendingReward, { isLoading: isPaying }] = usePayPendingRewardMutation();
  const [declinePendingReward, { isLoading: isDeclining }] = useDeclinePendingRewardMutation();
  const [resendGiftCardEmail, { isLoading: isResending }] = useResendGiftCardEmailMutation();
  const [cancelGiftCardReward, { isLoading: isCancelling }] = useCancelGiftCardRewardMutation();
  const { data: reclaimedRewards = [], isLoading: isLoadingClaims } = useGetReclaimedRewardsQuery();
  const [markDiscountApplied] = useMarkDiscountAppliedMutation();

  // Local state for editable reward configs
  const [localRewardConfigs, setLocalRewardConfigs] = useState<RewardConfig[]>([]);
  const [currency, setCurrency] = useState<'EUR' | 'USD' | 'GBP'>('EUR');

  // Sync local state with API data
  useEffect(() => {
    if (rewardSettings) {
      setLocalRewardConfigs(rewardSettings.reward_configs || []);
      setCurrency(rewardSettings.currency as 'EUR' | 'USD' | 'GBP');
    }
  }, [rewardSettings]);

  const currencySymbols: Record<string, string> = {
    EUR: '€',
    USD: '$',
    GBP: '£'
  };

  // Optimistic state for rewards and discounts
  const rewards = rewardsData?.content || [];
  const [optimisticRewards, setOptimisticRewardStatus] = useOptimistic(
    rewards,
    (currentRewards, { rewardId, newStatus }: { rewardId: string; newStatus: string }) =>
      currentRewards.map(r => r.id === rewardId ? { ...r, status: newStatus } : r)
  );

  // Filter rewards
  const filteredRewards = optimisticRewards.filter(reward => {
    const matchesFilter = filterStatus === 'all' || reward.status === filterStatus;
    const matchesSearch = searchTerm === '' ||
      (reward.promoter_name && reward.promoter_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (reward.prospect_name && reward.prospect_name.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesFilter && matchesSearch;
  });

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      discount_pending: 'bg-orange-100 text-orange-800',
      discount_applied: 'bg-emerald-100 text-emerald-800',
      choice_pending: 'bg-purple-100 text-purple-800',
      payment_failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-200 text-gray-700'
    };

    const labels: Record<string, string> = {
      paid: t('rewards.status.paid'),
      pending: t('rewards.status.pending'),
      processing: t('rewards.status.processing'),
      completed: t('rewards.status.completed'),
      rejected: t('rewards.status.rejected'),
      discount_pending: t('rewards.status.discountPending'),
      discount_applied: t('rewards.status.discountApplied'),
      choice_pending: t('rewards.status.choicePending'),
      payment_failed: t('rewards.status.paymentFailed'),
      cancelled: t('rewards.status.cancelled')
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const handleEditReward = (rewardConfig: RewardConfig) => {
    setSelectedRewardType({
      id: rewardConfig.id,
      name: rewardConfig.name,
      description: rewardConfig.description,
      amount: rewardConfig.amount,
      icon: rewardConfig.icon,
      color: rewardConfig.color,
      type: rewardConfig.type,
      auto_approve: rewardConfig.auto_approve ?? false,
      max_times: rewardConfig.max_times ?? 1,
      reward_method: rewardConfig.reward_method || 'GIFT_CARD',
      review_platform: rewardConfig.review_platform || 'CLAREEFAI',
      g2_review_url: rewardConfig.g2_review_url ?? null,
      g2_amount: rewardConfig.g2_amount ?? null,
      reward_policy_trigger: rewardConfig.reward_policy_trigger || 'LEAD_SUBMISSION',
      lead_submission_amount: rewardConfig.lead_submission_amount ?? null,
      lead_submission_reward_method: rewardConfig.lead_submission_reward_method || 'GIFT_CARD',
      lead_submission_discount_type: rewardConfig.lead_submission_discount_type ?? null,
      lead_submission_discount_value: rewardConfig.lead_submission_discount_value ?? null,
      lead_submission_discount_product_name: rewardConfig.lead_submission_discount_product_name ?? null,
      lead_submission_discount_name: rewardConfig.lead_submission_discount_name ?? null,
      deal_won_amount: rewardConfig.deal_won_amount ?? null,
      deal_won_reward_method: rewardConfig.deal_won_reward_method || 'GIFT_CARD',
      deal_won_discount_type: rewardConfig.deal_won_discount_type ?? null,
      deal_won_discount_value: rewardConfig.deal_won_discount_value ?? null,
      deal_won_discount_product_name: rewardConfig.deal_won_discount_product_name ?? null,
      deal_won_discount_name: rewardConfig.deal_won_discount_name ?? null,
      discount_type: rewardConfig.discount_type,
      discount_value: rewardConfig.discount_value,
      discount_product_name: rewardConfig.discount_product_name,
      discount_name: rewardConfig.discount_name,
    });
    setShowEditModal(true);
  };

  const handleSaveReward = async (updatedReward: any) => {
    // Update local state
    const updatedConfigs = localRewardConfigs.map(config =>
      config.id === updatedReward.id
        ? {
          ...config,
          name: updatedReward.name,
          description: updatedReward.description,
          amount: updatedReward.amount,
          icon: updatedReward.icon ?? config.icon,
          color: updatedReward.color ?? config.color,
          auto_approve: updatedReward.auto_approve ?? false,
          max_times: updatedReward.max_times ?? 1,
          reward_method: updatedReward.reward_method,
          review_platform: updatedReward.review_platform,
          g2_review_url: updatedReward.g2_review_url,
          g2_amount: updatedReward.g2_amount,
          reward_policy_trigger: updatedReward.reward_policy_trigger,
          lead_submission_amount: updatedReward.lead_submission_amount,
          lead_submission_reward_method: updatedReward.lead_submission_reward_method,
          lead_submission_discount_type: updatedReward.lead_submission_discount_type,
          lead_submission_discount_value: updatedReward.lead_submission_discount_value,
          lead_submission_discount_product_name: updatedReward.lead_submission_discount_product_name,
          lead_submission_discount_name: updatedReward.lead_submission_discount_name,
          deal_won_amount: updatedReward.deal_won_amount,
          deal_won_reward_method: updatedReward.deal_won_reward_method,
          deal_won_discount_type: updatedReward.deal_won_discount_type,
          deal_won_discount_value: updatedReward.deal_won_discount_value,
          deal_won_discount_product_name: updatedReward.deal_won_discount_product_name,
          deal_won_discount_name: updatedReward.deal_won_discount_name,
          discount_type: updatedReward.discount_type,
          discount_value: updatedReward.discount_value,
          discount_product_name: updatedReward.discount_product_name,
          discount_name: updatedReward.discount_name,
        }
        : config
    );
    setLocalRewardConfigs(updatedConfigs);

    // Save to backend
    try {
      await updateRewardSettings({
        currency,
        reward_configs: updatedConfigs
      }).unwrap();
      refetchSettings();
    } catch (error) {
      console.error('Failed to update reward settings:', error);
    }

    setShowEditModal(false);
    setSelectedRewardType(null);
  };

  const handleDeleteReward = async (rewardId: string) => {
    const updatedConfigs = localRewardConfigs.filter(config => config.id !== rewardId);
    setLocalRewardConfigs(updatedConfigs);
    try {
      await updateRewardSettings({
        currency,
        reward_configs: updatedConfigs
      }).unwrap();
      refetchSettings();
      toast.success(t('rewards.customRewardDeleted'));
    } catch (error) {
      console.error('Failed to delete reward:', error);
      toast.error(t('rewards.failedToDeleteReward'));
    }
    setShowEditModal(false);
    setSelectedRewardType(null);
  };

  const handleCurrencyChange = async (newCurrency: 'EUR' | 'USD' | 'GBP') => {
    setCurrency(newCurrency);
    try {
      await updateRewardSettings({
        currency: newCurrency,
        reward_configs: localRewardConfigs
      }).unwrap();
      refetchSettings();
    } catch (error) {
      console.error('Failed to update currency:', error);
    }
  };


  const getRewardIcon = (iconName: string, size = 'w-6 h-6') => {
    switch (iconName) {
      case 'star': return <Star className={size} />;
      case 'users': return <Users className={size} />;
      case 'video': return <Video className={size} />;
      case 'trending': return <TrendingUp className={size} />;
      case 'file': return <FileText className={size} />;
      case 'heart': return <Heart className={size} />;
      case 'zap': return <Zap className={size} />;
      case 'award': return <Award className={size} />;
      case 'gift': return <Gift className={size} />;
      case 'target': return <Target className={size} />;
      case 'sparkles': return <Sparkles className={size} />;
      case 'trophy': return <Trophy className={size} />;
      case 'thumbsup': return <ThumbsUp className={size} />;
      case 'smile': return <Smile className={size} />;
      case 'coffee': return <Coffee className={size} />;
      case 'lightbulb': return <Lightbulb className={size} />;
      case 'music': return <Music className={size} />;
      case 'mic': return <Mic className={size} />;
      case 'bookopen': return <BookOpen className={size} />;
      case 'messagecircle': return <MessageCircle className={size} />;
      default: return <Star className={size} />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'processing':
        return <CreditCard className="w-4 h-4 text-blue-600" />;
      case 'rejected':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getIconColor = (configType: string, configColor?: string) => {
    if (configType === 'CUSTOM' && configColor) return configColor;
    switch (configType) {
      case 'REVIEW':
        return 'bg-blue-500';
      case 'REFERENCE_CALL':
        return 'bg-purple-500';
      case 'VIDEO':
        return 'bg-red-500';
      case 'REFERRAL':
        return 'bg-orange-500';
      case 'USE_CASE':
        return 'bg-green-500';
      case 'CUSTOM':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getAmountChipColor = (rewardMethod: string | undefined, discountType?: string | null) => {
    if (rewardMethod === 'DISCOUNT' || (rewardMethod === 'BOTH' && discountType)) {
      if (discountType === 'free_months') return 'bg-violet-100 text-violet-700';
      if (discountType === 'credit_points') return 'bg-orange-100 text-orange-700';
      return 'bg-orange-100 text-orange-700'; // percentage
    }
    return 'bg-emerald-100 text-emerald-700'; // gift card / currency amount
  };

  // Resolves where the "view delivered action" eye should take the vendor for a given reward.
  //  - REVIEW/VIDEO/USE_CASE carry the embedded Testimonial id → deep-link to the in-app page
  //    (the detail routes also need the promoter id, which we hand over via nav state).
  //  - G2 reviews republish nothing in-app, so we open the promoter's pasted proof link instead.
  //  - Referral / reference-call / bonus / custom have no viewable page → no eye (returns null).
  const getRewardActionTarget = (
    reward: RewardResponse
  ):
    | { kind: 'route'; to: string; state: Record<string, unknown> }
    | { kind: 'external'; href: string }
    | null => {
    const type = reward.type?.toLowerCase();
    if (type === 'review' && reward.review_platform === 'G2' && reward.evidence) {
      return { kind: 'external', href: reward.evidence };
    }
    // The detail pages load via { promoterId, testimonialId } and read promoterId from a
    // *nested* state object (state.review / state.testimonial), so the shape matters.
    if (reward.testimonial_id && reward.promoter_id) {
      const promoter = { promoterId: reward.promoter_id };
      switch (type) {
        case 'review':
          return { kind: 'route', to: `/reviews/${reward.testimonial_id}`, state: { review: promoter } };
        case 'video':
          return { kind: 'route', to: `/testimonials/${reward.testimonial_id}`, state: { testimonial: promoter } };
        case 'use_case':
          return { kind: 'route', to: `/use-case-submission/${reward.testimonial_id}`, state: { testimonial: promoter } };
      }
    }
    // Last resort: any reward that still carries a proof link (e.g. legacy rows) opens it.
    return reward.evidence ? { kind: 'external', href: reward.evidence } : null;
  };

  // Renders the eye control for a reward, or nothing when there's no action to view.
  const renderViewActionEye = (reward: RewardResponse) => {
    const target = getRewardActionTarget(reward);
    if (!target) return null;
    const common = {
      title: t('rewards.viewAction'),
      'aria-label': t('rewards.viewAction'),
      className: actionIconBtn,
    };
    if (target.kind === 'external') {
      return (
        <a href={target.href} target="_blank" rel="noopener noreferrer" {...common}>
          <Eye className="w-4 h-4" />
        </a>
      );
    }
    return (
      <button
        type="button"
        onClick={() => navigate(target.to, { state: target.state })}
        {...common}
      >
        <Eye className="w-4 h-4" />
      </button>
    );
  };

  const renderLoading = () => (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      <span className="ml-2 text-gray-600">{t('common.loading')}</span>
    </div>
  );

  const renderOverview = () => {
    if (isLoadingSettings) return renderLoading();

    return (
      <div className="space-y-8">
        {/* Balance Warning Banner */}
        {balanceData && balanceData.balance <= 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-900">{t('rewards.insufficientBalance')}</p>
                <p className="text-sm text-amber-700">{t('rewards.insufficientBalanceDesc')}</p>
              </div>
            </div>
            <button
              onClick={() => setActiveTab('funding')}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
            >
              {t('rewards.addFunds')}
            </button>
          </div>
        )}

        {/* Reward Types Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...localRewardConfigs].sort((a, b) => {
            const order = ['REVIEW', 'REFERENCE_CALL', 'VIDEO', 'USE_CASE', 'REFERRAL', 'CUSTOM'];
            const ai = order.indexOf(a.type);
            const bi = order.indexOf(b.type);
            return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
          }).map((config) => (
            <div key={config.id} className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 ${getIconColor(config.type, config.color)} rounded-lg flex items-center justify-center`}>
                    <div className="text-white">
                      {getRewardIcon(config.icon)}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{config.type === 'CUSTOM' ? config.name : t(`rewards.types.${config.type}.name`, { defaultValue: config.name })}</h3>
                    <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{config.type === 'CUSTOM' ? config.description : t(`rewards.types.${config.type}.description`, { defaultValue: config.description })}</p>
                  </div>
                </div>
                {canManageReward && (
                  <button
                    onClick={() => handleEditReward(config)}
                    className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                {(config.reward_method === 'GIFT_CARD' || config.reward_method === 'BOTH' || !config.reward_method) && (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-lg font-bold ${getAmountChipColor('GIFT_CARD')}`}>
                    <CreditCard className="w-4 h-4" />
                    {config.amount} {currency}
                  </span>
                )}
                {config.reward_method === 'BOTH' && config.discount_value && (
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{t('rewards.or')}</span>
                )}
                {(config.reward_method === 'DISCOUNT' || config.reward_method === 'BOTH') && config.discount_value && (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-lg font-bold ${getAmountChipColor('DISCOUNT', config.discount_type)}`}>
                    {config.discount_type === 'percentage' && <Percent className="w-4 h-4" />}
                    {config.discount_type === 'credit_points' && <Coins className="w-4 h-4" />}
                    {config.discount_type === 'free_months' && <CalendarDays className="w-4 h-4" />}
                    {config.discount_type === 'percentage'
                      ? t('rewards.discount.percentOff', { value: config.discount_value })
                      : config.discount_type === 'credit_points'
                        ? t('rewards.discount.points', { value: config.discount_value })
                        : t('rewards.discount.freeMonths', { value: config.discount_value })}
                  </span>
                )}
                <span className="text-xs text-gray-400">{t('rewards.perAction')}{config.reward_method === 'BOTH' ? ` · ${t('rewards.promoterChooses')}` : ''}</span>
              </div>

              <div className="flex flex-wrap gap-1.5">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${config.auto_approve
                    ? 'bg-green-50 text-green-700'
                    : 'bg-amber-50 text-amber-700'
                  }`}>
                  <ShieldCheck className="w-3 h-3 mr-1" />
                  {config.auto_approve ? t('rewards.autoApproved') : t('rewards.manual')}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600">
                  <RotateCcw className="w-3 h-3 mr-1" />
                  {t('rewards.maxTimes', { count: config.max_times ?? 1 })}
                </span>
              </div>
            </div>
          ))}

          {/* Create Custom Reward Card */}
          {canManageReward && (
            <div
              onClick={() => navigate('/rewards/create-custom')}
              className="border-2 border-dashed border-purple-300 bg-purple-50/30 rounded-xl p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-all group min-h-[180px]"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-purple-200 transition-colors">
                <Plus className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('rewards.createCustomReward')}</h3>
              <p className="text-sm text-gray-500 mb-4">{t('rewards.createCustomRewardDesc')}</p>
              <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium group-hover:bg-purple-700 transition-colors">
                <Plus className="w-4 h-4" />
                {t('rewards.createRewardCta')}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRewardsList = () => {
    if (isLoadingRewards) return renderLoading();

    return (
      <div className="space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-500">{t('rewards.list.totalEarned')}</p>
            <p className="text-2xl font-bold text-gray-900">{(rewardsSummary?.total_earned || 0).toLocaleString()}{currencySymbols[currency]}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-500">{t('rewards.list.paid')}</p>
            <p className="text-2xl font-bold text-green-600">{(rewardsSummary?.paid || 0).toLocaleString()}{currencySymbols[currency]}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-500">{t('rewards.list.pending')}</p>
            <p className="text-2xl font-bold text-yellow-600">{(rewardsSummary?.pending || 0).toLocaleString()}{currencySymbols[currency]}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-500">{t('rewards.list.thisMonth')}</p>
            <p className="text-2xl font-bold text-blue-600">{(rewardsSummary?.current_month || 0).toLocaleString()}{currencySymbols[currency]}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('rewards.list.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="all">{t('rewards.list.allStatuses')}</option>
            <option value="paid">{t('rewards.status.paid')}</option>
            <option value="pending">{t('rewards.status.pending')}</option>
            <option value="completed">{t('rewards.status.completed')}</option>
            <option value="rejected">{t('rewards.status.rejected')}</option>
            <option value="discount_pending">{t('rewards.status.discountPending')}</option>
            <option value="discount_applied">{t('rewards.status.discountApplied')}</option>
            <option value="choice_pending">{t('rewards.status.choicePending')}</option>
          </select>
        </div>

        {/* Rewards table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.list.columns.promoter')}</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.list.columns.type')}</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.list.columns.details')}</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.list.columns.amount')}</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.list.columns.date')}</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.list.columns.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRewards.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-gray-500">
                      <Gift className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p>{t('rewards.list.noRewards')}</p>
                    </td>
                  </tr>
                ) : (
                  filteredRewards.map((reward) => (
                    <tr key={reward.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900">{reward.promoter_name || t('rewards.list.unknown')}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getIconColor(reward.type)} text-white`}>
                          {reward.type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 max-w-[200px] truncate">
                        {reward.prospect_name ? `${reward.prospect_name}${reward.prospect_company ? ` · ${reward.prospect_company}` : ''}` : reward.details || '—'}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-semibold text-gray-900">{reward.amount}{currencySymbols[currency]}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {new Date(reward.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(reward.status)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderPayments = () => {
    if (isLoadingRewards) return renderLoading();

    return (
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900">{t('rewards.payments.title')}</h3>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={t('rewards.payments.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-10 w-full sm:w-64 pl-10 pr-9 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder:text-gray-400 transition-[box-shadow,border-color] duration-150 ease-out focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/30"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    aria-label={t('common.clear')}
                    title={t('common.clear')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-400 transition-[transform,background-color,color] duration-150 ease-out hover:bg-gray-100 hover:text-gray-600 active:scale-[0.96]"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="relative">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="h-10 w-full sm:w-auto appearance-none cursor-pointer rounded-lg border border-gray-300 bg-white pl-3 pr-9 text-sm text-gray-900 transition-[box-shadow,border-color] duration-150 ease-out focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/30"
                >
                  <option value="all">{t('rewards.payments.allStatuses')}</option>
                  <option value="paid">{t('rewards.status.paid')}</option>
                  <option value="pending">{t('rewards.status.pending')}</option>
                  <option value="completed">{t('rewards.status.completed')}</option>
                  <option value="rejected">{t('rewards.status.rejected')}</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              </div>

              <button className="inline-flex items-center justify-center gap-2 h-10 pl-3.5 pr-4 rounded-lg bg-green-600 text-white text-sm font-medium whitespace-nowrap hover:bg-green-700 transition-[transform,background-color] duration-150 ease-out active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-green-500">
                <Download className="w-4 h-4" />
                <span>{t('rewards.payments.export')}</span>
              </button>
            </div>
          </div>

          {/* Payments table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{t('rewards.list.columns.promoter')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{t('rewards.list.columns.type')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{t('rewards.list.columns.amount')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{t('rewards.list.columns.date')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{t('rewards.list.columns.status')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{t('rewards.list.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRewards.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-500">{t('rewards.list.noRewards')}</td>
                  </tr>
                ) : (
                  filteredRewards.map((reward) => (
                    <tr key={reward.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div>
                          <div className="font-medium text-gray-900">{reward.promoter_name || t('rewards.list.unknown')}</div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <div className="font-medium text-gray-900">{reward.type}</div>
                          {reward.prospect_name && (
                            <div className="text-sm text-gray-600">
                              {reward.prospect_name} ({reward.prospect_company})
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-bold text-green-600 tabular-nums">{reward.amount}{currencySymbols[currency]}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm text-gray-900 tabular-nums">
                          {new Date(reward.date).toLocaleDateString('en-US')}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(reward.status)}
                          {getStatusBadge(reward.status)}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          {renderViewActionEye(reward)}
                          {reward.status === 'pending' && reward.id && (
                            <>
                              <button
                                onClick={async () => {
                                  setOptimisticRewardStatus({ rewardId: reward.id!, newStatus: 'paid' });
                                  try {
                                    await payPendingReward(reward.id!).unwrap();
                                    toast.success(t('rewards.payments.paidSuccess', { amount: `${reward.amount}${currencySymbols[currency]}` }));
                                  } catch (err: any) {
                                    toast.error(err?.data?.message || err?.message || t('rewards.payments.failedToPay'));
                                  }
                                }}
                                disabled={isPaying}
                                className={`${actionBtnBase} ${actionBtnVariants.green}`}
                              >
                                <CreditCard className="w-4 h-4" />
                                <span>{t('rewards.payments.pay')}</span>
                              </button>
                              <button
                                onClick={async () => {
                                  setOptimisticRewardStatus({ rewardId: reward.id!, newStatus: 'rejected' });
                                  try {
                                    await declinePendingReward(reward.id!).unwrap();
                                    toast.success(t('rewards.payments.declinedSuccess'));
                                  } catch (err: any) {
                                    toast.error(err?.data?.message || err?.message || t('rewards.payments.failedToDecline'));
                                  }
                                }}
                                disabled={isDeclining}
                                className={`${actionBtnBase} ${actionBtnVariants.red}`}
                              >
                                <XCircle className="w-4 h-4" />
                                <span>{t('rewards.payments.decline')}</span>
                              </button>
                            </>
                          )}
                          {reward.status === 'paid' && reward.reward_method !== 'DISCOUNT' && reward.id && (
                            <button
                              onClick={async () => {
                                try {
                                  await resendGiftCardEmail(reward.id!).unwrap();
                                  toast.success(t('rewards.payments.emailResent'));
                                } catch (err: any) {
                                  toast.error(err?.data?.message || err?.message || t('rewards.payments.failedToResend'));
                                }
                              }}
                              disabled={isResending}
                              className={`${actionBtnBase} ${actionBtnVariants.blue}`}
                            >
                              <Mail className="w-4 h-4" />
                              <span>{t('rewards.payments.resend')}</span>
                            </button>
                          )}
                          {reward.status === 'paid' && reward.reward_method !== 'DISCOUNT' && reward.id && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(t('rewards.payments.cancelConfirm'))) return;
                                try {
                                  await cancelGiftCardReward(reward.id!).unwrap();
                                  toast.success(t('rewards.payments.cancelledSuccess'));
                                } catch (err: any) {
                                  toast.error(err?.data?.message || err?.message || t('rewards.payments.failedToCancel'));
                                }
                              }}
                              disabled={isCancelling}
                              title={t('rewards.payments.cancelHint')}
                              className={`${actionBtnBase} ${actionBtnVariants.red}`}
                            >
                              <XCircle className="w-4 h-4" />
                              <span>{t('rewards.payments.cancel')}</span>
                            </button>
                          )}
                          {reward.status === 'paid' && reward.reward_method !== 'DISCOUNT' && (
                            <a
                              href={TREMENDOUS_DASHBOARD_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={t('rewards.payments.cancelInDashboardHint')}
                              className={`${actionBtnBase} ${actionBtnVariants.outline}`}
                            >
                              <ExternalLink className="w-4 h-4" />
                              <span>{t('rewards.payments.cancelInDashboard')}</span>
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderAnalytics = () => {
    if (isLoadingAnalytics) return renderLoading();

    const analytics = rewardAnalytics;

    return (
      <div className="space-y-6">
        {/* Key metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{t('rewards.analytics.totalPaid')}</p>
                <p className="text-3xl font-bold text-gray-900">{(analytics?.total_paid || 0).toLocaleString()}{currencySymbols[currency]}</p>
                <p className="text-sm text-gray-500 mt-1">{t('rewards.analytics.pendingLabel', { amount: `${(analytics?.total_pending || 0).toLocaleString()}${currencySymbols[currency]}` })}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <Target className="w-7 h-7 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{t('rewards.analytics.thisMonth')}</p>
                <p className="text-3xl font-bold text-gray-900">{(analytics?.total_this_month || 0).toLocaleString()}{currencySymbols[currency]}</p>
                <p className="text-sm text-gray-500 mt-1">{t('rewards.analytics.totalRewards', { count: analytics?.total_rewards_count || 0 })}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <BarChart3 className="w-7 h-7 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{t('rewards.analytics.activePromoters')}</p>
                <p className="text-3xl font-bold text-gray-900">{analytics?.active_promoters_count || 0}</p>
                <p className="text-sm text-gray-500 mt-1">{t('rewards.analytics.avgReward', { amount: `${analytics?.avg_reward_amount || 0}${currencySymbols[currency]}` })}</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <Users className="w-7 h-7 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
              {t('rewards.analytics.rewardsByType')}
            </h3>
            <div className="space-y-3">
              {(analytics?.rewards_by_type || []).map((item) => {
                const maxAmount = Math.max(...(analytics?.rewards_by_type || []).map(i => i.total_amount), 1);
                const percentage = (item.total_amount / maxAmount) * 100;
                return (
                  <div key={item.type}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{item.type.replace('_', ' ')}</span>
                      <span className="text-sm text-gray-500">{item.count} · {item.total_amount}{currencySymbols[currency]}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className={`h-2 rounded-full ${getIconColor(item.type)}`} style={{ width: `${percentage}%` }} />
                    </div>
                  </div>
                );
              })}
              {(!analytics?.rewards_by_type || analytics.rewards_by_type.length === 0) && (
                <p className="text-center text-gray-500 py-4">{t('rewards.analytics.noData')}</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <PieChart className="w-5 h-5 mr-2 text-green-600" />
              {t('rewards.analytics.monthlyTrend')}
            </h3>
            <div className="space-y-3">
              {(analytics?.monthly_trend || []).map((item) => (
                <div key={item.month} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-sm font-medium text-gray-700">{item.month}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500">{t('rewards.analytics.rewardsCount', { count: item.count })}</span>
                    <span className="text-sm font-semibold text-gray-900">{item.amount}{currencySymbols[currency]}</span>
                  </div>
                </div>
              ))}
              {(!analytics?.monthly_trend || analytics.monthly_trend.length === 0) && (
                <p className="text-center text-gray-500 py-4">{t('rewards.analytics.noData')}</p>
              )}
            </div>
          </div>
        </div>

        {/* Top performers */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Award className="w-5 h-5 mr-2 text-purple-600" />
            {t('rewards.analytics.topPerformers')}
          </h3>

          <div className="space-y-3">
            {(analytics?.top_performers || []).map((performer, index) => (
              <div key={performer.promoter_id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs ${index === 0 ? 'bg-yellow-500' :
                      index === 1 ? 'bg-gray-400' :
                        index === 2 ? 'bg-orange-600' :
                          'bg-gray-300'
                    }`}>
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">{performer.promoter_name}</h4>
                    {performer.promoter_company && (
                      <p className="text-xs text-gray-500">{performer.promoter_company}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-gray-900">{performer.total_earned}{currencySymbols[currency]}</div>
                  <div className="text-xs text-gray-500">{t('rewards.analytics.rewardsCount', { count: performer.rewards_count })}</div>
                </div>
              </div>
            ))}
            {(!analytics?.top_performers || analytics.top_performers.length === 0) && (
              <p className="text-center text-gray-500 py-4">{t('rewards.analytics.noTopPerformers')}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFunding = () => {
    if (isLoadingFundRequests) return renderLoading();

    const fundRequests = fundRequestsData || [];

    // Filter fund requests
    const filteredFundRequests = fundRequests.filter(request => {
      const matchesFilter = fundFilterStatus === 'all' || request.status === fundFilterStatus;
      return matchesFilter;
    });

    const getFundRequestStatusBadge = (status: string) => {
      const badges: Record<string, string> = {
        PENDING: 'bg-yellow-100 text-yellow-800',
        ACCEPTED: 'bg-green-100 text-green-800',
        REJECTED: 'bg-red-100 text-red-800'
      };

      const labels: Record<string, string> = {
        PENDING: t('rewards.funding.status.pending'),
        ACCEPTED: t('rewards.funding.status.accepted'),
        REJECTED: t('rewards.funding.status.rejected')
      };

      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
          {labels[status] || status}
        </span>
      );
    };

    return (
      <div className="space-y-6">
        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{fundRequests.length}</div>
            <div className="text-sm text-gray-600">{t('rewards.funding.totalRequests')}</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-yellow-600">{fundRequests.filter(r => r.status === 'PENDING').length}</div>
            <div className="text-sm text-gray-600">{t('rewards.funding.pending')}</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-green-600">{fundRequests.filter(r => r.status === 'ACCEPTED').length}</div>
            <div className="text-sm text-gray-600">{t('rewards.funding.accepted')}</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-red-600">{fundRequests.filter(r => r.status === 'REJECTED').length}</div>
            <div className="text-sm text-gray-600">{t('rewards.funding.rejected')}</div>
          </div>
        </div>

        {/* Fund requests list */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900">{t('rewards.funding.fundRequests')}</h3>

            <div className="flex items-center space-x-4">
              <select
                value={fundFilterStatus}
                onChange={(e) => setFundFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">{t('rewards.funding.allStatuses')}</option>
                <option value="PENDING">{t('rewards.funding.status.pending')}</option>
                <option value="ACCEPTED">{t('rewards.funding.status.accepted')}</option>
                <option value="REJECTED">{t('rewards.funding.status.rejected')}</option>
              </select>
            </div>
          </div>

          {/* Fund requests table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{t('rewards.funding.columns.date')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{t('rewards.funding.columns.amount')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{t('rewards.funding.columns.message')}</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">{t('rewards.funding.columns.status')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredFundRequests.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-gray-500">
                      {t('rewards.funding.noRequests')}
                    </td>
                  </tr>
                ) : (
                  filteredFundRequests.map((request) => (
                    <tr key={request.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div className="text-sm text-gray-900">
                          {new Date(request.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-bold text-green-600">{request.amount.toFixed(2)}{currencySymbols[currency]}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm text-gray-700">
                          {request.message ? (
                            <span>{request.message}</span>
                          ) : (
                            <span className="text-gray-400 italic">{t('rewards.funding.noMessage')}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {getFundRequestStatusBadge(request.status)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const pendingClaimsCount = reclaimedRewards.filter(r => r.status === 'pending' || r.status === 'discount_pending').length;
  // Rewards awaiting the vendor's manual approval on the Payments tab — drives the "unread mailbox"
  // count badge so a vendor knows there are payments to action without opening the tab.
  const pendingPaymentsCount = optimisticRewards.filter(r => r.status === 'pending').length;

  const renderClaims = () => {
    if (isLoadingClaims) return renderLoading();

    return (
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-500">{t('rewards.claims.totalClaims')}</p>
            <p className="text-2xl font-bold text-gray-900">{reclaimedRewards.length}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-500">{t('rewards.claims.pending')}</p>
            <p className="text-2xl font-bold text-yellow-600">{pendingClaimsCount}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-500">{t('rewards.claims.resolved')}</p>
            <p className="text-2xl font-bold text-green-600">{reclaimedRewards.filter(r => r.status === 'paid' || r.status === 'completed' || r.status === 'discount_applied').length}</p>
          </div>
        </div>

        {/* Claims table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.claims.columns.promoter')}</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.claims.columns.type')}</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.claims.columns.description')}</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.claims.columns.amount')}</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.claims.columns.date')}</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.claims.columns.status')}</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{t('rewards.claims.columns.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reclaimedRewards.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-500">
                      <HandCoins className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p>{t('rewards.claims.noClaims')}</p>
                    </td>
                  </tr>
                ) : (
                  reclaimedRewards.map((claim) => (
                    <tr
                      key={claim.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedClaim(claim)}
                    >
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900">{claim.promoter_name || t('rewards.claims.unknown')}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getIconColor(claim.type.toUpperCase())} text-white`}>
                          {claim.type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 max-w-[250px] truncate">
                        {claim.details || '—'}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-semibold text-gray-900">{claim.amount}{currencySymbols[currency]}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {new Date(claim.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(claim.status)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                          {claim.status === 'pending' && claim.id && (
                            <>
                              <button
                                onClick={async () => {
                                  setOptimisticRewardStatus({ rewardId: claim.id!, newStatus: 'paid' });
                                  try {
                                    await payPendingReward(claim.id!).unwrap();
                                    toast.success(t('rewards.claims.paidSuccess', { amount: `${claim.amount}${currencySymbols[currency]}` }));
                                  } catch (err: any) {
                                    toast.error(err?.data?.message || err?.message || t('rewards.claims.failedToPay'));
                                  }
                                }}
                                disabled={isPaying}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <CreditCard className="w-4 h-4" />
                                <span>{t('rewards.claims.pay')}</span>
                              </button>
                              <button
                                onClick={async () => {
                                  setOptimisticRewardStatus({ rewardId: claim.id!, newStatus: 'rejected' });
                                  try {
                                    await declinePendingReward(claim.id!).unwrap();
                                    toast.success(t('rewards.claims.declinedSuccess'));
                                  } catch (err: any) {
                                    toast.error(err?.data?.message || err?.message || t('rewards.claims.failedToDecline'));
                                  }
                                }}
                                disabled={isDeclining}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <XCircle className="w-4 h-4" />
                                <span>{t('rewards.claims.decline')}</span>
                              </button>
                            </>
                          )}
                          {claim.status === 'discount_pending' && claim.id && (
                            <button
                              onClick={async () => {
                                try {
                                  await markDiscountApplied(claim.id!).unwrap();
                                  toast.success(t('rewards.claims.discountMarked'));
                                } catch (err: any) {
                                  toast.error(err?.data?.message || err?.message || t('rewards.claims.failedToMark'));
                                }
                              }}
                              className="flex items-center space-x-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span>{t('rewards.claims.markDone')}</span>
                            </button>
                          )}
                          {renderViewActionEye(claim)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Claim Detail Modal */}
        {selectedClaim && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedClaim(null)}>
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">{t('rewards.claims.detailTitle')}</h3>
                <button onClick={() => setSelectedClaim(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{t('rewards.claims.promoter')}</p>
                    <p className="font-medium text-gray-900">{selectedClaim.promoter_name || t('rewards.claims.unknown')}</p>
                  </div>
                  {selectedClaim.promoter_id && (
                    <button
                      onClick={() => {
                        setSelectedClaim(null);
                        navigate(`/promoters/${selectedClaim.promoter_id}`);
                      }}
                      className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <span>{t('rewards.claims.viewProfile')}</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('rewards.claims.activityType')}</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getIconColor(selectedClaim.type.toUpperCase())} text-white mt-1`}>
                      {selectedClaim.type.replace('_', ' ')}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('rewards.claims.amount')}</p>
                    <p className="text-lg font-bold text-gray-900">{selectedClaim.amount}{currencySymbols[currency]}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('rewards.claims.claimedDate')}</p>
                    <p className="font-medium text-gray-900">{new Date(selectedClaim.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('rewards.claims.status')}</p>
                    <div className="mt-1">{getStatusBadge(selectedClaim.status)}</div>
                  </div>
                </div>

                {selectedClaim.details && (
                  <div>
                    <p className="text-sm text-gray-500">{t('rewards.claims.description')}</p>
                    <p className="text-gray-700 mt-1 bg-gray-50 rounded-lg p-3 text-sm">{selectedClaim.details}</p>
                  </div>
                )}

                {(selectedClaim.prospect_name || selectedClaim.prospect_company) && (
                  <div>
                    <p className="text-sm text-gray-500">{t('rewards.claims.prospect')}</p>
                    <p className="font-medium text-gray-900 mt-1">
                      {selectedClaim.prospect_name}{selectedClaim.prospect_company ? ` · ${selectedClaim.prospect_company}` : ''}
                    </p>
                  </div>
                )}

                {selectedClaim.reward_method && (
                  <div>
                    <p className="text-sm text-gray-500">{t('rewards.claims.rewardMethod')}</p>
                    <p className="font-medium text-gray-900 mt-1 capitalize">{selectedClaim.reward_method.replace('_', ' ')}</p>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                {selectedClaim.status === 'pending' && selectedClaim.id && (
                  <>
                    <button
                      onClick={async () => {
                        setOptimisticRewardStatus({ rewardId: selectedClaim.id!, newStatus: 'rejected' });
                        try {
                          await declinePendingReward(selectedClaim.id!).unwrap();
                          toast.success(t('rewards.claims.declinedSuccess'));
                          setSelectedClaim(null);
                        } catch (err: any) {
                          toast.error(err?.data?.message || err?.message || t('rewards.claims.failedToDecline'));
                        }
                      }}
                      disabled={isDeclining}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircle className="w-4 h-4" />
                      <span>{t('rewards.claims.decline')}</span>
                    </button>
                    <button
                      onClick={async () => {
                        setOptimisticRewardStatus({ rewardId: selectedClaim.id!, newStatus: 'paid' });
                        try {
                          await payPendingReward(selectedClaim.id!).unwrap();
                          toast.success(t('rewards.claims.paidSuccess', { amount: `${selectedClaim.amount}${currencySymbols[currency]}` }));
                          setSelectedClaim(null);
                        } catch (err: any) {
                          toast.error(err?.data?.message || err?.message || t('rewards.claims.failedToPay'));
                        }
                      }}
                      disabled={isPaying}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CreditCard className="w-4 h-4" />
                      <span>{t('rewards.claims.payReward')}</span>
                    </button>
                  </>
                )}
                {selectedClaim.status === 'discount_pending' && selectedClaim.id && (
                  <button
                    onClick={async () => {
                      try {
                        await markDiscountApplied(selectedClaim.id!).unwrap();
                        toast.success(t('rewards.claims.discountMarked'));
                        setSelectedClaim(null);
                      } catch (err: any) {
                        toast.error(err?.data?.message || err?.message || t('rewards.claims.failedToMark'));
                      }
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>{t('rewards.claims.markDone')}</span>
                  </button>
                )}
                <button
                  onClick={() => setSelectedClaim(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageTitle title={t('rewards.pageTitle')} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('rewards.management')}</h1>
          <p className="text-gray-600 mt-1">{t('rewards.subtitle')}</p>
        </div>
        <div className="flex items-center space-x-3">
          {balanceData && (
            <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
              <Wallet className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-500">{t('rewards.balance')}</span>
              <span className={`text-sm font-semibold ${balanceData.balance <= 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {balanceData.balance.toFixed(2)}€
              </span>
            </div>
          )}
          <button
            onClick={() => setShowAddFundRequestModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
          >
            <Wallet className="w-4 h-4" />
            <span>{t('rewards.addFundRequest')}</span>
          </button>
        </div>
      </div>

      {/* Navigation tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Award className="w-4 h-4" />
                <span>{t('rewards.tabs.overview')}</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('rewards_list')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'rewards_list'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Gift className="w-4 h-4" />
                <span>{t('rewards.tabs.rewardsList')}</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('payments')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'payments'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                {/* Unread-mailbox metaphor: an envelope (with the count) when payments await approval,
                    falling back to the neutral card icon once the inbox is clear. */}
                {pendingPaymentsCount > 0 ? <Mail className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
                <span className={pendingPaymentsCount > 0 ? 'font-semibold' : undefined}>{t('rewards.tabs.payments')}</span>
                {pendingPaymentsCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-purple-600 text-white text-[10px] font-bold tabular-nums leading-none">
                    {pendingPaymentsCount}
                  </span>
                )}
              </div>
            </button>

            <button
              onClick={() => setActiveTab('claims')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'claims'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <HandCoins className="w-4 h-4" />
                <span>{t('rewards.tabs.claims')}</span>
                {pendingClaimsCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-orange-500 text-white text-[10px] font-bold tabular-nums leading-none">
                    {pendingClaimsCount}
                  </span>
                )}
              </div>
            </button>

            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'analytics'
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-4 h-4" />
                <span>{t('rewards.tabs.analytics')}</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('funding')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'funding'
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Wallet className="w-4 h-4" />
                <span>{t('rewards.tabs.funding')}</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('specific_rewards')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'specific_rewards'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4" />
                <span>{t('rewards.tabs.specificRewards')}</span>
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'rewards_list' && renderRewardsList()}
          {activeTab === 'payments' && renderPayments()}
          {activeTab === 'claims' && renderClaims()}
          {activeTab === 'analytics' && renderAnalytics()}
          {activeTab === 'funding' && renderFunding()}
          {activeTab === 'specific_rewards' && <SpecificRewardsListTab />}
        </div>
      </div>

      <EditRewardModal
        rewardType={selectedRewardType}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveReward}
        onDelete={handleDeleteReward}
        currencySymbol={currencySymbols[currency]}
        currency={currency}
        products={vendor?.pageInfo?.products?.filter(p => p.name.trim()) || []}
      />

      <AddFundRequestModal
        isOpen={showAddFundRequestModal}
        onClose={() => setShowAddFundRequestModal(false)}
      />

    </div>
  );
};

export default Rewards;
